// 节点归一化：把后端各种 nodeType / nodeKind / resourceType 统一映射到 4 种原子 kind
// kind: 'image' | 'video' | 'text' | 'audio'
// 容器布局：layout: { type: 'grid' | 'sequence', cols?, children? }
// 兼容旧数据：保留 legacyType 便于回滚和过渡期的特殊渲染

const VIDEO_EXT = /\.(mp4|mov|webm|m4v|avi)(\?|$)/i;
const AUDIO_EXT = /\.(mp3|wav|m4a|aac|ogg|flac)(\?|$)/i;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?|$)/i;

function inferKindFromUrl(url) {
  if (!url) return null;
  if (VIDEO_EXT.test(url)) return 'video';
  if (AUDIO_EXT.test(url)) return 'audio';
  if (IMAGE_EXT.test(url)) return 'image';
  return null;
}

// 输入：后端 raw nodeData (JSON 解析后)，原始 backendType (node.nodeType)
// 输出：{ kind, status, layout?, content, generation?, legacyType, raw }
export function normalizeNodeData(rawData, backendType) {
  const data = rawData || {};
  const legacyType = backendType
    || data.nodeKind
    || (data.resourceType ? 'resource' : null)
    || (data.generateType ? 'generate' : 'resource');

  // 1) 推断 kind
  let kind;
  if (legacyType === 'aiDirector') {
    // AI 导演对话节点：无媒体产物，语义上按文本类处理
    kind = 'text';
  } else if (data.resourceType === 'image' || data.resourceType === 'video' || data.resourceType === 'audio' || data.resourceType === 'text') {
    kind = data.resourceType;
  } else if (data.generateType === 'video') {
    kind = 'video';
  } else if (data.generateType === 'image') {
    kind = 'image';
  } else if (legacyType === 'storyboardGrid' || legacyType === 'storyboardShot' || legacyType === 'storyboardEpisode') {
    kind = 'image';
  } else if (legacyType === 'seedanceMerge' || legacyType === 'motionStoryboard') {
    kind = 'video';
  } else {
    kind = inferKindFromUrl(data.url || data.thumbnail) || 'image';
  }

  // 2) 推断容器 layout
  let layout = null;
  if (legacyType === 'storyboardGrid') {
    layout = { type: 'grid', cols: 3, children: data.cells || data.prompts || [] };
  } else if (legacyType === 'storyboardEpisode') {
    layout = { type: 'sequence', children: data.shots || [] };
  }

  // 3) 状态推断
  const isValidUrl = (v) => v && (typeof v === 'string') && (v.startsWith('http') || v.startsWith('/') || v.startsWith('blob:'));
  const hasContent = !!(
    [data.url, data.thumbnail, data.lastResultUrl, data.result, data.generatedImage, data.generatedVideo, data.gridImageUrl].some(isValidUrl)
    || data.text
  );
  const isGenerating = !!(data.generating || data.isGenerating || data.status === 'generating');
  const status = isGenerating ? 'generating' : (hasContent ? 'ready' : 'empty');

  // 4) 内容（覆盖所有旧字段：url/thumbnail/lastResultUrl/result/generatedImage/generatedVideo）
  const resolvedUrl = [data.url, data.lastResultUrl, data.result, data.generatedImage, data.generatedVideo, data.gridImageUrl].find(isValidUrl) || null;
  const resolvedThumb = isValidUrl(data.thumbnail) ? data.thumbnail : resolvedUrl;
  const content = {
    url: resolvedUrl,
    text: data.text || data.description || null,
    thumbnail: resolvedThumb,
    duration: data.duration || null,
  };

  // 5) 生成元数据
  const isGenNode = !!data.generateType;
  const isVideoGen = data.generateType === 'video';
  const isVideoLegacy = legacyType === 'seedanceMerge' || legacyType === 'motionStoryboard';
  const resolvedPrompt = data.prompt || data.imagePrompt || data.videoPrompt || null;
  const generation = (resolvedPrompt || isGenNode || isVideoLegacy) ? {
    provider: (isVideoGen || isVideoLegacy) ? (data.videoProvider || data.provider || null) : null,
    model: (isVideoGen || isVideoLegacy) ? (data.videoModel || data.model || null) : null,
    prompt: resolvedPrompt,
    params: {
      aspectRatio: data.aspectRatio || null,
      duration: (isVideoGen || isVideoLegacy) ? (data.videoDuration || data.duration || null) : null,
      mode: (isVideoGen || isVideoLegacy) ? (data.videoMode || null) : null,
      sound: (isVideoGen || isVideoLegacy) ? (data.videoSound || null) : null,
    },
  } : null;

  return {
    kind,
    status,
    layout,
    content,
    generation,
    legacyType,
    name: data.name || null,
    description: data.description || null,
    raw: data,
  };
}

// 反向：归一化对象 → 写回后端的 data JSON 结构（保留 raw 字段防丢失）
export function denormalizeNodeData(normalized, patch = {}) {
  const merged = { ...normalized, ...patch };
  return {
    ...(merged.raw || {}),
    name: merged.name,
    description: merged.description,
    resourceType: merged.kind,
    url: merged.content?.url ?? merged.raw?.url,
    thumbnail: merged.content?.thumbnail ?? merged.raw?.thumbnail,
    text: merged.content?.text ?? merged.raw?.text,
    duration: merged.content?.duration ?? merged.raw?.duration,
    ...(merged.generation ? {
      prompt: merged.generation.prompt,
      videoProvider: merged.generation.provider,
      videoModel: merged.generation.model,
      videoDuration: merged.generation.params?.duration,
      aspectRatio: merged.generation.params?.aspectRatio,
      videoMode: merged.generation.params?.mode,
      videoSound: merged.generation.params?.sound,
    } : {}),
  };
}

// 类型兼容表：source.kind → 允许的 target.kind
export const COMPATIBILITY = {
  text: ['image', 'video'],
  image: ['image', 'video'],
  audio: ['video'],
  video: ['video'],
};

export function canConnect(sourceKind, targetKind) {
  return (COMPATIBILITY[sourceKind] || []).includes(targetKind);
}
